import { computed, signal } from '@preact/signals';

/** Evento do Chrome/Edge/Android que permite abrir o diálogo "Instalar app" no momento que escolhermos. */
type EventoInstalar = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

let evento: EventoInstalar | null = null;

/** O navegador já nos entregou o convite de instalação (Chrome, Edge, Android). */
export const podeInstalar = signal(false);

/** Já está rodando como app instalado (janela própria / tela inicial)? */
export function emModoApp(): boolean {
  if (typeof matchMedia !== 'function') return false;
  return matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
export const instalado = signal(emModoApp());

/** iPhone/iPad: o Safari não tem botão de instalar; é preciso "Compartilhar → Adicionar à Tela de Início". */
export const ehIos = typeof navigator !== 'undefined' && (/iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

/** Mostrar o botão "Instalar app"? Só se ainda não é app e há como instalar (convite do navegador ou iOS). */
export const mostrarInstalar = computed(() => !instalado.value && (podeInstalar.value || ehIos));

/** O aparelho está sem internet? (O painel mostra dados já carregados e avisa; alterar só funciona conectado.) */
export const semInternet = signal(typeof navigator !== 'undefined' && navigator.onLine === false);

/** Liga os avisos do navegador. Chamar logo ao abrir o app (o convite pode chegar antes de a tela desenhar). */
export function iniciarPwa() {
  window.addEventListener('online', () => (semInternet.value = false));
  window.addEventListener('offline', () => (semInternet.value = true));
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // guarda para usar quando a pessoa clicar, em vez do mini-aviso automático
    evento = e as EventoInstalar;
    podeInstalar.value = true;
  });
  window.addEventListener('appinstalled', () => {
    evento = null;
    podeInstalar.value = false;
    instalado.value = true;
  });
  matchMedia('(display-mode: standalone)').addEventListener('change', (e) => (instalado.value = e.matches || emModoApp()));
}

/** Abre o diálogo de instalação do navegador. */
export async function instalar(): Promise<'aceito' | 'recusado' | 'indisponivel'> {
  if (!evento) return 'indisponivel';
  const e = evento;
  evento = null; // o convite só vale uma vez
  podeInstalar.value = false;
  await e.prompt();
  const { outcome } = await e.userChoice;
  return outcome === 'accepted' ? 'aceito' : 'recusado';
}
